import {
    AdvancedSearchEntityType,
    WorkflowEventType,
    WorkflowStatus,
} from "generated/graphql";
import { CrmMode } from "util/crm-mode";
import {
    computeWorkflowDelay,
    entityTypeLabel,
    isTokenDelayRecipe,
} from "../manage-workflows-helper";

export interface WorkflowDelaySource {
    delay: number;
    delayRecipe: string | null;
}

export interface WorkflowChipSource extends WorkflowDelaySource {
    trigger: { eventType: WorkflowEventType };
    actions: ReadonlyArray<{
        __typename: "WorkflowActionEmailEntity" | "WorkflowActionTaskEntity";
    }>;
}

const WAITLESS_EVENT_TYPES: WorkflowEventType[] = [
    WorkflowEventType.EmailOpened,
    WorkflowEventType.Schedule,
];

export interface WorkflowStepChip {
    kind: "email" | "task";
    delayLabel: string | null;
}

export function delayChipLabel(workflow: WorkflowDelaySource): string | null {
    if (isTokenDelayRecipe(workflow.delayRecipe)) {
        return null;
    }
    const [delay, unit] = computeWorkflowDelay(workflow);
    if (delay === 0) {
        return "Immediately";
    }
    return `+${delay}${unit}`;
}

export function workflowStepChips(workflow: WorkflowChipSource): WorkflowStepChip[] {
    const labelsDelay = WAITLESS_EVENT_TYPES.indexOf(workflow.trigger.eventType) === -1;
    return workflow.actions.map((action, index) => ({
        kind: action.__typename === "WorkflowActionTaskEntity" ? "task" : "email",
        delayLabel: index === 0 && labelsDelay ? delayChipLabel(workflow) : null,
    }));
}

export function wizardStepSettings<T extends { scope: string }>(
    settings: readonly T[],
): T[] {
    return settings.filter(s => s.scope === "tenant");
}

export type StepTimingMode = "immediately" | "wait";

export function stepTimingMode(delay: number): StepTimingMode {
    return delay === 0 ? "immediately" : "wait";
}

export function filterWorkflows<
    T extends {
        name: string;
        attachTo: AdvancedSearchEntityType;
        status: WorkflowStatus;
    },
>(
    workflows: readonly T[],
    entityTypeFilter: AdvancedSearchEntityType | "all",
    statusFilter: WorkflowStatus | "all",
    searchText: string,
): T[] {
    return workflows.filter(
        workflow =>
            (entityTypeFilter === "all" || workflow.attachTo === entityTypeFilter) &&
            (statusFilter === "all" || workflow.status === statusFilter) &&
            workflow.name.toLowerCase().includes(searchText.toLowerCase()),
    );
}

export function presentEntityTypes<T extends { attachTo: AdvancedSearchEntityType }>(
    supportedEntityTypes: readonly AdvancedSearchEntityType[],
    workflows: readonly T[],
): AdvancedSearchEntityType[] {
    return supportedEntityTypes.filter(entityType =>
        workflows.some(workflow => workflow.attachTo === entityType),
    );
}

export function sortWorkflowsByRecentlyUpdated<T extends { updatedAt: string }>(
    workflows: readonly T[],
): T[] {
    return [...workflows].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
}

export function workflowEntityTypeLabel(
    attachTo: AdvancedSearchEntityType,
    mode: CrmMode,
): string {
    switch (attachTo) {
        case AdvancedSearchEntityType.Client:
            return mode === "combined" ? "Client - Placement" : "Client";
        case AdvancedSearchEntityType.HomeCareLead:
            return mode === "combined" ? "Client - Home Care" : "Client";
        default:
            return entityTypeLabel(attachTo);
    }
}
